import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  TouchableOpacity, 
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  Alert,
  Pressable
} from 'react-native';
import { 
  XStack, 
  YStack, 
  Button, 
  Dialog,
  SizableText
} from 'tamagui';
import { 
  MaterialIcons, 
  Feather, 
  AntDesign,
  MaterialCommunityIcons 
} from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DateTimePicker from '@react-native-community/datetimepicker';

interface Task {
  _id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in-progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string[];
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
  workPackageId: string;
  tags?: string[];
  attachments?: any[];
  comments?: any[];
  timeTracking?: {
    estimated: number;
    actual: number;
  };
}

interface ModernTaskModalProps {
  visible: boolean;
  task: Task | null;
  project: any;
  workPackages: any[];
  onClose: () => void;
  onSave: (taskData: Partial<Task>) => Promise<void>;
}

const ModernTaskModal: React.FC<ModernTaskModalProps> = ({
  visible,
  task,
  project,
  workPackages,
  onClose,
  onSave
}) => {
  const theme = themeSelector();
  const isEditing = !!task;

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<Task['status']>('todo');
  const [priority, setPriority] = useState<Task['priority']>('medium');
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [workPackageId, setWorkPackageId] = useState('');
  const [timeEstimate, setTimeEstimate] = useState('');
  const [loading, setLoading] = useState(false);

  // UI state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAssigneeSelector, setShowAssigneeSelector] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTag, setNewTag] = useState('');

  // Initialize form when task changes
  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setStatus(task.status || 'todo');
      setPriority(task.priority || 'medium');
      setDueDate(task.dueDate ? new Date(task.dueDate) : null);
      setAssignedTo(task.assignedTo || []);
      setTags(task.tags || []);
      setWorkPackageId(task.workPackageId || '');
      setTimeEstimate(task.timeTracking?.estimated?.toString() || '');
    } else {
      // Reset form for new task
      setTitle('');
      setDescription('');
      setStatus('todo');
      setPriority('medium');
      setDueDate(null);
      setAssignedTo([]);
      setTags([]);
      setWorkPackageId('');
      setTimeEstimate('');
    }
  }, [task]);

  const statusOptions = [
    { value: 'todo', label: 'To Do', color: '#6b7280', icon: 'radio-button-unchecked' },
    { value: 'in-progress', label: 'In Progress', color: '#3b82f6', icon: 'timelapse' },
    { value: 'review', label: 'Review', color: '#f59e0b', icon: 'rate-review' },
    { value: 'done', label: 'Done', color: '#10b981', icon: 'check-circle' },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: '#22c55e' },
    { value: 'medium', label: 'Medium', color: '#eab308' },
    { value: 'high', label: 'High', color: '#f97316' },
    { value: 'urgent', label: 'Urgent', color: '#ef4444' },
  ];

  const teamMembers = project?.team || [];

  const handleSave = async () => {
    if (!title.trim()) {
      Alert.alert('Error', 'Task title is required');
      return;
    }

    setLoading(true);
    try {
      const taskData: Partial<Task> = {
        title: title.trim(),
        description: description.trim(),
        status,
        priority,
        dueDate: dueDate?.toISOString(),
        assignedTo,
        tags,
        workPackageId: workPackageId || workPackages[0]?._id,
        timeTracking: {
          estimated: timeEstimate ? parseInt(timeEstimate) : 0,
          actual: task?.timeTracking?.actual || 0,
        },
      };

      await onSave(taskData);
      onClose();
    } catch (error) {
      Alert.alert('Error', 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
      setShowTagInput(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const toggleAssignee = (userId: string) => {
    setAssignedTo(current => 
      current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId]
    );
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 500,
              maxWidth: 700,
              maxHeight: "90%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" justifyContent="space-between">
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <MaterialIcons
                    name={isEditing ? "edit" : "add-task"}
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    {isEditing ? 'Edit Task' : 'Create Task'}
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    {isEditing ? 'Update task details' : 'Add a new task to your project'}
                  </Dialog.Description>
                </YStack>
              </XStack>
              <Pressable onPress={onClose}>
                <AntDesign 
                  name="close" 
                  size={24} 
                  color={theme ? '#6b7280' : '#9ca3af'} 
                />
              </Pressable>
            </XStack>
          </View>

          {/* Content */}
          <ScrollView style={{ maxHeight: 500 }} showsVerticalScrollIndicator={false}>
            <View style={{ padding: 24 }}>
              <YStack gap="$4">
                {/* Title */}
                <YStack gap="$2">
                  <SizableText
                    color={theme ? '#1f2937' : '#f9fafb'}
                    fontWeight="600"
                    size="$4"
                  >
                    Task Title *
                  </SizableText>
                  <TextInput
                    value={title}
                    onChangeText={setTitle}
                    placeholder="Enter task title..."
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                      fontWeight: '500',
                    }}
                  />
                </YStack>

                {/* Description */}
                <YStack gap="$2">
                  <SizableText
                    color={theme ? '#1f2937' : '#f9fafb'}
                    fontWeight="600"
                    size="$4"
                  >
                    Description
                  </SizableText>
                  <TextInput
                    value={description}
                    onChangeText={setDescription}
                    placeholder="Enter task description..."
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 100,
                      textAlignVertical: 'top',
                      fontWeight: '500',
                    }}
                    multiline
                    numberOfLines={4}
                  />
                </YStack>

                {/* Work Package Selection */}
                <YStack gap="$2">
                  <SizableText
                    color={theme ? '#1f2937' : '#f9fafb'}
                    fontWeight="600"
                    size="$4"
                  >
                    Work Package *
                  </SizableText>
                  {workPackages.length > 0 ? (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                      <XStack gap="$2">
                        {workPackages.map((wp) => (
                          <TouchableOpacity
                            key={wp._id}
                            onPress={() => setWorkPackageId(wp._id)}
                            style={{
                              paddingHorizontal: 16,
                              paddingVertical: 12,
                              borderRadius: 12,
                              backgroundColor: workPackageId === wp._id
                                ? '#3b82f6'
                                : theme ? '#f1f5f9' : '#4b5563',
                              borderWidth: 1,
                              borderColor: workPackageId === wp._id
                                ? '#3b82f6'
                                : theme ? '#e2e8f0' : '#6b7280',
                              minWidth: 120,
                              alignItems: 'center',
                            }}
                          >
                            <Text style={{
                              fontSize: 14,
                              fontWeight: '600',
                              color: workPackageId === wp._id ? '#ffffff' : theme ? '#1f2937' : '#f9fafb',
                              textAlign: 'center',
                            }}>
                              {wp.work_package_title || wp.title || 'Untitled'}
                            </Text>
                            {wp.task_list && (
                              <Text style={{
                                fontSize: 12,
                                color: workPackageId === wp._id ? '#e0f2fe' : theme ? '#64748b' : '#94a3b8',
                                marginTop: 2,
                              }}>
                                {wp.task_list.length} tasks
                              </Text>
                            )}
                          </TouchableOpacity>
                        ))}
                      </XStack>
                    </ScrollView>
                  ) : (
                    <View style={{
                      padding: 16,
                      backgroundColor: theme ? '#fef3c7' : '#451a03',
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: theme ? '#fbbf24' : '#d97706',
                      alignItems: 'center',
                    }}>
                      <MaterialIcons
                        name="warning"
                        size={20}
                        color={theme ? "#f59e0b" : "#fbbf24"}
                      />
                      <Text style={{
                        fontSize: 14,
                        color: theme ? '#92400e' : '#fbbf24',
                        marginTop: 4,
                        textAlign: 'center',
                        fontWeight: '500',
                      }}>
                        No work packages available. Create a work package first.
                      </Text>
                    </View>
                  )}
                </YStack>

                {/* Status and Priority Row */}
                <XStack gap="$4">
                  {/* Status */}
                  <YStack flex={1} gap="$2">
                    <SizableText
                      color={theme ? '#1f2937' : '#f9fafb'}
                      fontWeight="600"
                      size="$4"
                    >
                      Status
                    </SizableText>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                      <XStack gap="$2">
                        {statusOptions.map((option) => (
                          <TouchableOpacity
                            key={option.value}
                            onPress={() => setStatus(option.value as Task['status'])}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 6,
                              paddingHorizontal: 12,
                              paddingVertical: 8,
                              borderRadius: 20,
                              backgroundColor: status === option.value 
                                ? option.color 
                                : theme ? '#f1f5f9' : '#4b5563',
                              borderWidth: 1,
                              borderColor: status === option.value 
                                ? option.color 
                                : theme ? '#e2e8f0' : '#6b7280',
                            }}
                          >
                            <MaterialIcons 
                              name={option.icon as any} 
                              size={16} 
                              color={status === option.value ? '#ffffff' : option.color} 
                            />
                            <Text style={{
                              fontSize: 14,
                              fontWeight: '500',
                              color: status === option.value ? '#ffffff' : theme ? '#1f2937' : '#f9fafb',
                            }}>
                              {option.label}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </XStack>
                    </ScrollView>
                  </YStack>

                  {/* Priority */}
                  <YStack flex={1} gap="$2">
                    <SizableText
                      color={theme ? '#1f2937' : '#f9fafb'}
                      fontWeight="600"
                      size="$4"
                    >
                      Priority
                    </SizableText>
                    <XStack gap="$2" flexWrap="wrap">
                      {priorityOptions.map((option) => (
                        <TouchableOpacity
                          key={option.value}
                          onPress={() => setPriority(option.value as Task['priority'])}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 6,
                            paddingHorizontal: 12,
                            paddingVertical: 8,
                            borderRadius: 20,
                            backgroundColor: priority === option.value
                              ? option.color 
                              : theme ? '#f1f5f9' : '#4b5563',
                            borderWidth: 1,
                            borderColor: priority === option.value
                              ? option.color 
                              : theme ? '#e2e8f0' : '#6b7280',
                          }}
                        >
                          <View style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: priority === option.value ? '#ffffff' : option.color,
                          }} />
                          <Text style={{
                            fontSize: 14,
                            fontWeight: '500',
                            color: priority === option.value ? '#ffffff' : theme ? '#1f2937' : '#f9fafb',
                            textTransform: 'capitalize',
                          }}>
                            {option.label}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </XStack>
                  </YStack>
                </XStack>

                {/* Due Date */}
                <YStack gap="$2">
                  <SizableText
                    color={theme ? '#1f2937' : '#f9fafb'}
                    fontWeight="600"
                    size="$4"
                  >
                    Due Date
                  </SizableText>
                  <TouchableOpacity
                    onPress={() => setShowDatePicker(true)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      padding: 16,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                    }}
                  >
                    <MaterialIcons 
                      name="date-range" 
                      size={20} 
                      color={theme ? '#6b7280' : '#9ca3af'} 
                    />
                    <Text style={{
                      fontSize: 16,
                      color: dueDate 
                        ? (theme ? '#1f2937' : '#f9fafb')
                        : (theme ? '#9ca3af' : '#6b7280'),
                      fontWeight: '500',
                    }}>
                      {dueDate 
                        ? dueDate.toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })
                        : 'Select due date'
                      }
                    </Text>
                    {dueDate && (
                      <TouchableOpacity
                        onPress={() => setDueDate(null)}
                        style={{ marginLeft: 'auto' }}
                      >
                        <MaterialIcons 
                          name="clear" 
                          size={20} 
                          color={theme ? '#6b7280' : '#9ca3af'} 
                        />
                      </TouchableOpacity>
                    )}
                  </TouchableOpacity>
                </YStack>

                {/* Time Estimate */}
                <YStack gap="$2">
                  <SizableText
                    color={theme ? '#1f2937' : '#f9fafb'}
                    fontWeight="600"
                    size="$4"
                  >
                    Time Estimate (hours)
                  </SizableText>
                  <TextInput
                    value={timeEstimate}
                    onChangeText={setTimeEstimate}
                    placeholder="Enter estimated hours..."
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                    keyboardType="numeric"
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                      fontWeight: '500',
                    }}
                  />
                </YStack>

                {/* Tags */}
                <YStack gap="$2">
                  <XStack justifyContent="space-between" alignItems="center">
                    <SizableText
                      color={theme ? '#1f2937' : '#f9fafb'}
                      fontWeight="600"
                      size="$4"
                    >
                      Tags
                    </SizableText>
                    <TouchableOpacity
                      onPress={() => setShowTagInput(true)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 8,
                        backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                        borderWidth: 1,
                        borderColor: theme ? '#e2e8f0' : '#6b7280',
                      }}
                    >
                      <MaterialIcons 
                        name="add" 
                        size={16} 
                        color={theme ? '#3b82f6' : '#60a5fa'} 
                      />
                      <Text style={{
                        fontSize: 12,
                        fontWeight: '600',
                        color: theme ? '#3b82f6' : '#60a5fa',
                      }}>
                        Add Tag
                      </Text>
                    </TouchableOpacity>
                  </XStack>

                  {showTagInput && (
                    <XStack gap="$2" alignItems="center">
                      <TextInput
                        value={newTag}
                        onChangeText={setNewTag}
                        placeholder="Enter tag name..."
                        placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                        style={{
                          flex: 1,
                          fontSize: 14,
                          color: theme ? '#1f2937' : '#f9fafb',
                          backgroundColor: theme ? '#f8fafc' : '#374151',
                          borderWidth: 1,
                          borderColor: theme ? '#e5e7eb' : '#4b5563',
                          borderRadius: 8,
                          padding: 12,
                          fontWeight: '500',
                        }}
                        onSubmitEditing={addTag}
                        autoFocus
                      />
                      <TouchableOpacity onPress={addTag}>
                        <MaterialIcons name="check" size={20} color="#10b981" />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => {
                        setShowTagInput(false);
                        setNewTag('');
                      }}>
                        <MaterialIcons name="close" size={20} color="#ef4444" />
                      </TouchableOpacity>
                    </XStack>
                  )}

                  {tags.length > 0 && (
                    <XStack gap="$2" flexWrap="wrap">
                      {tags.map((tag) => (
                        <TouchableOpacity
                          key={tag}
                          onPress={() => removeTag(tag)}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 4,
                            paddingHorizontal: 12,
                            paddingVertical: 6,
                            backgroundColor: theme ? '#e0f2fe' : '#0c4a6e',
                            borderRadius: 16,
                            borderWidth: 1,
                            borderColor: theme ? '#0ea5e9' : '#38bdf8',
                          }}
                        >
                          <Text style={{
                            fontSize: 12,
                            color: theme ? '#0c4a6e' : '#e0f2fe',
                            fontWeight: '500',
                          }}>
                            {tag}
                          </Text>
                          <MaterialIcons 
                            name="close" 
                            size={14} 
                            color={theme ? '#0c4a6e' : '#e0f2fe'} 
                          />
                        </TouchableOpacity>
                      ))}
                    </XStack>
                  )}
                </YStack>
              </YStack>
            </View>
          </ScrollView>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={onClose}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Cancel
                  </Text>
                </Pressable>
              </Dialog.Close>
              <Pressable
                onPress={handleSave}
                disabled={loading || !title.trim() || !workPackageId}
                style={{
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                  borderRadius: 12,
                  backgroundColor: loading || !title.trim() || !workPackageId
                    ? theme ? '#cbd5e1' : '#4b5563'
                    : theme ? '#3b82f6' : '#2563eb',
                  minWidth: 100,
                  alignItems: 'center',
                  elevation: 2,
                  shadowColor: '#3b82f6',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.2,
                  shadowRadius: 8,
                  opacity: loading || !title.trim() || !workPackageId ? 0.5 : 1,
                }}
              >
                <Text style={{
                  color: '#ffffff',
                  fontWeight: '600',
                  fontSize: 14,
                }}>
                  {loading ? 'Saving...' : (isEditing ? 'Update Task' : 'Create Task')}
                </Text>
              </Pressable>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>

      {/* Date Picker */}
      {showDatePicker && (
        <DateTimePicker
          value={dueDate || new Date()}
          mode="date"
          display="default"
          onChange={(event, selectedDate) => {
            setShowDatePicker(false);
            if (selectedDate) {
              setDueDate(selectedDate);
            }
          }}
        />
      )}
    </Dialog>
  );
};

export default ModernTaskModal;
